import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import {
  EXCHANGE_SECTION_TYPES,
  type ExchangeAsset,
  type ExchangeAsset as ExchangeAssetType,
  type ExchangeInputSectionType,
} from '../model';
import {
  AssetBaseInfo,
  Button,
  FormFieldSearchableSelect,
  NumberInput,
} from '@/shared/components';
import { useCallback, useMemo, type ReactNode, type ChangeEvent } from 'react';
import MaxIcon from '@/shared/assets/max-icon.svg?react';
import { NUMERIC_CONFIG } from '@/shared/config';
import {
  ExchangeAssetOption,
  ExchangeQuickSelectSection,
} from '@/features/exchange';

type Props = {
  type: ExchangeInputSectionType;
  title: string;
  description: ReactNode | string;
  amount: string;
  resetFormErrors: () => void;
  setAmount: (amount: string) => void;
  assetsOptions: Array<ExchangeAssetType>;
  onAssetChange?: (asset: string) => void;
  onAmountBlur?: () => void;
  isAmountError?: boolean;
  isSelectError?: boolean;
  precision?: number;
  selectedAsset?: ExchangeAsset;
  onMaxClick?: () => void;
  onQuickSelect: (asset: string) => void;
};

export type ExchangeAssetSelectItem = ExchangeAssetType & {
  value: string;
};

const exchangeSectionStyles = tv({
  slots: {
    section: 'flex flex-col gap-[1rem] pb-[1.4rem]',
    sectionTitle: 'headline4',
    sectionDescription: 'p4 text-secondary h-[2rem]',
    amountInputContainer: 'flex gap-[0.8rem] h-[4rem] items-stretch',
    amountInput: 'hero3 bg-transparent border-none flex-1 min-w-0',
    currencySelector:
      'rounded-full h-[4rem] pl-[1.6rem] pr-[0.8rem] [&_label]:text-primary [&_label]:!text-[1.6rem] [&_label]:!font-semibold',
    currencySelectorContent: 'rounded-[2.4rem] w-[35rem] max-w-[35rem]',
  },
  variants: {
    isAmountError: {
      true: {
        amountInput: 'text-destructive',
        sectionDescription: 'text-destructive',
      },
    },
    isSelectError: {
      true: {
        sectionDescription: 'text-destructive',
        currencySelector: 'bg-light-error',
      },
    },
  },
});

const ExchangeInputSection = ({
  type,
  title,
  description,
  amount,
  resetFormErrors,
  setAmount,
  assetsOptions,
  isAmountError = false,
  isSelectError = false,
  onAssetChange,
  onAmountBlur,
  precision = NUMERIC_CONFIG.precision.default,
  selectedAsset,
  onMaxClick,
  onQuickSelect,
}: Props) => {
  const { t } = useTranslation();

  const {
    section,
    sectionTitle,
    sectionDescription,
    amountInputContainer,
    amountInput,
    currencySelector,
    currencySelectorContent,
  } = exchangeSectionStyles({ isAmountError, isSelectError });

  const selectItems = useMemo(
    () =>
      assetsOptions.map(asset => ({
        ...asset,
        value: asset.code,
      })),
    [assetsOptions]
  );

  const renderItemCallback = useCallback(
    (item: ExchangeAssetSelectItem) => (
      <ExchangeAssetOption type={type} asset={item} />
    ),
    [type]
  );

  const selectedAssetMap = useMemo(() => {
    const map = new Map<string, ExchangeAssetType>();
    assetsOptions.forEach(asset => {
      map.set(asset.code, asset);
    });
    return map;
  }, [assetsOptions]);

  const renderSelectedValueCallback = useCallback(
    (value: string) => (
      <AssetBaseInfo
        showName={false}
        asset={selectedAssetMap.get(value)}
        iconClassName="size-[2.4rem]"
      />
    ),
    [selectedAssetMap]
  );

  const searchFilter = useCallback(
    ({ code, name }: ExchangeAssetSelectItem, search: string) =>
      [code, name].some(value =>
        value.toLowerCase().includes(search.toLowerCase())
      ),
    []
  );

  const isOptionSelected = useCallback(
    (item: ExchangeAssetSelectItem, value: string) => item.code === value,
    []
  );

  const handleAssetChange = useCallback(
    (value: string) => {
      resetFormErrors();
      onAssetChange?.(value);
    },
    [resetFormErrors, onAssetChange]
  );

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
  };

  return (
    <section className={section()}>
      <h4 className={sectionTitle()}>{title}</h4>
      <div className={amountInputContainer()}>
        <NumberInput
          value={amount}
          onChange={handleAmountChange}
          onBlur={onAmountBlur}
          placeholder={'0,0'}
          className={amountInput()}
          decimalScale={selectedAsset?.precision || precision}
        />
        <FormFieldSearchableSelect<ExchangeAssetSelectItem>
          name={type === EXCHANGE_SECTION_TYPES.FROM ? 'assetFrom' : 'assetTo'}
          placeholder={t('common.select')}
          items={selectItems}
          renderItem={renderItemCallback}
          renderSelectedValue={renderSelectedValueCallback}
          className={currencySelector()}
          contentClassName={currencySelectorContent()}
          contentViewportClassName={currencySelectorContent()}
          contentAlign="end"
          searchFilter={searchFilter}
          onValueChange={handleAssetChange}
          withCancelButton={true}
          getItemKey={item => type + item.code}
          isOptionSelected={isOptionSelected}
        />
      </div>

      {!selectedAsset && (
        <ExchangeQuickSelectSection
          assetsOptions={assetsOptions}
          type={type}
          onSelect={onQuickSelect}
        />
      )}

      <div className={'flex items-center p4 text-secondary gap-[0.8rem]'}>
        <span className={sectionDescription()}>{description}</span>
        {onMaxClick &&
          type === 'from' &&
          selectedAsset &&
          selectedAsset?.available &&
          selectedAsset?.available > 0 && (
            <Button
              type="button"
              variant="outline"
              className="shadow-smooth h-[2.6rem] border-none px-[1rem] py-[0.8rem] rounded-[1.6rem] text-foreground headline2"
              onClick={onMaxClick}>
              <MaxIcon className="w-[3.2rem]" />
            </Button>
          )}
      </div>
    </section>
  );
};

export { ExchangeInputSection };
